// Schritt 1 des Übergabe-Assistenten — Rahmen: Art, Objekt, Einheit, Datum/Uhrzeit
// und die drei Parteien (Übergeber, Übernehmer, Zeuge). Vorbelegung des Mieters aus
// dem laufenden Vertrag der gewählten Einheit (vertraege.einheit ist Freitext, kein
// FK — Abgleich läuft daher tolerant über passtZurEinheit() gegen die Bezeichnung aus
// objekt_einheiten, da die Schreibweise in den Stammdaten uneinheitlich ist) sowie der
// Vorgänger-Verweis bei Auszug (jüngstes abgeschlossenes Einzugsprotokoll derselben
// Einheit). Der Zeuge wird bereits beim Anlegen des Entwurfs mit dem eingeloggten
// Mitarbeiter vorbelegt (UebergabeAssistent.tsx — dort allein ist userProfile bekannt).
//
// Optik: Frosted Glass (Vorlage KontaktDetail.tsx/uebersicht-helpers.tsx) — kleine
// Karten je Partei, halbtransparente Eingabefelder, Gold nur für aktive Zustände.
//
// Fehlerbehandlung (Fix-Runde 1): Objekt- und Einheiten-Liste sind die einzigen hier
// geladenen Daten, die der Nutzer aktiv braucht (Auswahl treffen) — deren Fehler
// werden sichtbar gemacht (LadeHinweis + Neuversuch). Die Vorbelegung aus dem Vertrag
// und der Vorgänger-Verweis laufen dagegen leise im Hintergrund und dürfen bei einem
// Fehler einfach ausbleiben (kein Pflichtfeld, kein eigener Ladezustand in der UI, der
// sonst fälschlich "leer" statt "Fehler" anzeigen würde) — dort genügt die Prüfung von
// `error`, damit kein kaputter Datensatz weiterverarbeitet wird.
import React, { useCallback, useEffect, useState, type CSSProperties } from 'react';
import { AddressBook, Buildings, Door, EnvelopeSimple, Eye, HandArrowDown, HandArrowUp, HouseLine, MapPin, Phone, User, type Icon } from '@phosphor-icons/react';
import { GlasAuswahl } from '../../../components/ui/GlasAuswahl';
import { IosFeldZeile, IosGruppe } from '../../../components/ui/IosGruppe';
import { ArtKacheln } from './liste/ListenTeile';
import { supabase } from '../../../lib/supabase/client';
import type { LokalerEntwurf } from '../../../lib/uebergabe/lokal';
import type { Art, Partei } from '../../../lib/uebergabe/typen';
import { passtZurEinheit } from '../../../lib/uebergabe/einheitVergleich';
import { entferneUnpassendeMerkmale, istVermieterseite, parteiLabel, setzeParteiFeld, zeigtNeueAnschrift, type ParteiFeld } from '../../../lib/uebergabe/unterschriften';
import { suchePersonen, type PersonTreffer } from '../../../lib/kontakte/personenSuche';
import LadeHinweis from './LadeHinweis';

interface SchrittRahmenProps {
  entwurf: LokalerEntwurf;
  setEntwurf: (updater: LokalerEntwurf | ((vorher: LokalerEntwurf) => LokalerEntwurf)) => void;
  /** Hausmeister-App: Art, Objekt und Einheit nur anzeigen – die legt die Verwaltung fest
   *  (die Datenbank lehnt Änderungen ohnehin ab, Trigger uebergabe_bezug_schuetzen). */
  bezugGesperrt?: boolean;
}

interface ObjektOption { id: string; bezeichnung: string; strasse: string; hausnummer: string; plz: string; stadt: string }
interface EinheitOption { id: string; bezeichnung: string }

// Token-Stil (design_handoff_uebergabe §4)
const KARTE: CSSProperties = { borderRadius: 26, padding: 18, display: 'flex', flexDirection: 'column', gap: 14 };
const LABEL: CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--ink3)' };

// Rollen-App-Icons (Handoff §4): Übergeber orange, Übernehmer grün, Zeuge grau
const ROLLE_ICON: Record<Partei['rolle'], { icon: Icon; farbe: string }> = {
  uebergeber: { icon: HandArrowUp, farbe: '#ff9500' },
  uebernehmer: { icon: HandArrowDown, farbe: '#34c759' },
  zeuge: { icon: Eye, farbe: '#8e8e93' },
};

const isoZuDatetimeLocal = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const datetimeLocalZuIso = (wert: string): string => {
  const d = new Date(wert);
  return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
};

const QUELLE_LABEL: Record<PersonTreffer['quelle'], string> = { mieter: 'Mieter', eigentuemer: 'Eigentümer', kontakt: 'Kontakt' };

// Personen-Typeahead je Partei-Karte: sucht in Mietern/Eigentümern/Kontakten und
// übernimmt per Klick alle vier Felder (Spec: Karte entspricht danach der Person).
const ParteiSuche: React.FC<{ onWahl: (t: PersonTreffer) => void }> = ({ onWahl }) => {
  const [query, setQuery] = useState('');
  const [treffer, setTreffer] = useState<PersonTreffer[]>([]);
  useEffect(() => {
    if (query.trim().length < 2) { setTreffer([]); return; }
    let abgebrochen = false;
    const t = setTimeout(async () => {
      const r = await suchePersonen(query);
      if (!abgebrochen) setTreffer(r);
    }, 300);
    return () => { abgebrochen = true; clearTimeout(t); };
  }, [query]);
  return (
    <div className="relative">
      <label className="flex items-center" style={{ height: 44, padding: '0 12px', gap: 8, borderRadius: 13, background: 'var(--fill2)' }}>
        <AddressBook size={16} color="var(--ink3)" className="shrink-0" />
        <input value={query} onChange={e => setQuery(e.target.value)}
          onKeyDown={e => { if (e.key === 'Escape' && query) { e.stopPropagation(); setQuery(''); setTreffer([]); } }} placeholder="Aus Kontakten übernehmen…" aria-label="Aus Kontakten übernehmen"
          className="min-w-0 flex-1" style={{ border: 'none', outline: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 14, fontWeight: 600, color: 'var(--ink)' }} />
      </label>
      {treffer.length > 0 && (
        <div className="immo-scroll absolute left-0 right-0 z-20 flex flex-col overflow-y-auto"
          style={{ top: 'calc(100% + 4px)', maxHeight: 240, padding: 4, borderRadius: 16, background: 'var(--sheet)', boxShadow: '0 18px 40px -12px var(--shade), inset 0 0 0 1px var(--line)' }}>
          {treffer.map(t => (
            <button key={`${t.quelle}-${t.id}`} type="button" onClick={() => { onWahl(t); setQuery(''); setTreffer([]); }}
              className="immo-menue-zeile flex flex-col text-left"
              style={{ minHeight: 44, padding: '8px 10px', gap: 2, borderRadius: 12, border: 'none', background: 'transparent', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
              <span className="flex min-w-0 items-center" style={{ gap: 8 }}>
                <span className="truncate" style={{ fontSize: 14, fontWeight: 800 }}>{t.name}</span>
                <span className="shrink-0" style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 11, fontWeight: 800, lineHeight: '20px', background: 'var(--acc-soft)', color: 'var(--acc)' }}>
                  {QUELLE_LABEL[t.quelle]}
                </span>
              </span>
              {t.email && <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)' }}>{t.email}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const SchrittRahmen: React.FC<SchrittRahmenProps> = ({ entwurf, setEntwurf, bezugGesperrt = false }) => {
  const { protokoll } = entwurf;
  const [objekte, setObjekte] = useState<ObjektOption[]>([]);
  const [objekteFehler, setObjekteFehler] = useState(false);
  const [einheiten, setEinheiten] = useState<EinheitOption[]>([]);
  const [einheitenLaden, setEinheitenLaden] = useState(false);
  const [einheitenFehler, setEinheitenFehler] = useState(false);

  const ladeObjekte = useCallback(async () => {
    setObjekteFehler(false);
    const { data, error } = await supabase
      .from('objekte')
      .select('id, bezeichnung, strasse, hausnummer, plz, stadt')
      .order('bezeichnung');
    if (error) { setObjekteFehler(true); return; }
    setObjekte((data ?? []) as ObjektOption[]);
  }, []);
  useEffect(() => { void ladeObjekte(); }, [ladeObjekte]);

  const ladeEinheiten = useCallback(async () => {
    if (!protokoll.objekt_id) { setEinheiten([]); setEinheitenFehler(false); return; }
    setEinheitenLaden(true);
    setEinheitenFehler(false);
    const { data, error } = await supabase
      .from('objekt_einheiten')
      .select('id, bezeichnung')
      .eq('objekt_id', protokoll.objekt_id)
      .order('bezeichnung');
    setEinheitenLaden(false);
    if (error) { setEinheitenFehler(true); return; }
    setEinheiten((data ?? []) as EinheitOption[]);
  }, [protokoll.objekt_id]);
  useEffect(() => { void ladeEinheiten(); }, [ladeEinheiten]);

  // Vorbelegung: laufender Vertrag der Einheit liefert den Mieter — bei Auszug als
  // Übergeber, bei Einzug als Übernehmer. Bereits von Hand ausgefüllte Namen werden
  // nicht überschrieben (Einheit lässt sich nachträglich korrigieren, ohne Eingaben
  // zu verlieren). Best-effort im Hintergrund — Fehler brechen nur diesen Vorschlag
  // ab, ohne eigene Fehleranzeige (kein Pflichtfeld).
  useEffect(() => {
    const { objekt_id, einheit_id, art } = protokoll;
    if (!objekt_id || !einheit_id || art === 'kauf') return;
    let abgebrochen = false;
    (async () => {
      const { data: einheit, error: einheitFehler } = await supabase
        .from('objekt_einheiten').select('bezeichnung').eq('id', einheit_id).maybeSingle();
      if (abgebrochen || einheitFehler || !einheit?.bezeichnung) return;
      // Alle aktiven Verträge des Objekts laden und clientseitig über passtZurEinheit
      // gegen die Einheiten-Bezeichnung abgleichen statt serverseitig auf Gleichheit —
      // vertraege.einheit ist Freitext und in den Stammdaten uneinheitlich geschrieben.
      const { data: vertraege, error: vertragFehler } = await supabase
        .from('vertraege')
        .select('id, mieter_id, einheit')
        .eq('objekt_id', objekt_id).eq('status', 'aktiv')
        .order('beginn', { ascending: false });
      if (abgebrochen || vertragFehler) return;
      const vertrag = (vertraege ?? []).find(v => passtZurEinheit(v.einheit, einheit.bezeichnung));
      if (!vertrag?.mieter_id) return;
      const { data: mieter, error: mieterFehler } = await supabase
        .from('mieter').select('vorname, nachname, email, telefon').eq('id', vertrag.mieter_id).maybeSingle();
      if (abgebrochen || mieterFehler || !mieter) return;
      const rolle: Partei['rolle'] = art === 'auszug' ? 'uebergeber' : 'uebernehmer';
      setEntwurf(e => ({
        ...e,
        protokoll: {
          ...e.protokoll,
          mieter_id: vertrag.mieter_id,
          vertrag_id: vertrag.id,
          parteien: e.protokoll.parteien.map(p => (p.rolle === rolle && !p.name.trim())
            ? { ...p, name: `${mieter.vorname ?? ''} ${mieter.nachname ?? ''}`.trim(), email: mieter.email ?? '', telefon: mieter.telefon ?? '' }
            : p),
        },
      }));
    })();
    return () => { abgebrochen = true; };
  }, [protokoll.objekt_id, protokoll.einheit_id, protokoll.art, setEntwurf]);

  // Bei Auszug: jüngstes abgeschlossenes Einzugsprotokoll derselben Einheit als
  // Vorgänger — ebenfalls best-effort, darf leer bleiben.
  useEffect(() => {
    if (protokoll.art !== 'auszug' || !protokoll.einheit_id) return;
    let abgebrochen = false;
    (async () => {
      const { data, error } = await supabase.from('uebergabeprotokolle')
        .select('id, durchgefuehrt_am')
        .eq('einheit_id', protokoll.einheit_id).eq('art', 'einzug').eq('status', 'abgeschlossen')
        .order('durchgefuehrt_am', { ascending: false }).limit(1).maybeSingle();
      if (abgebrochen || error) return;
      setEntwurf(e => ({ ...e, protokoll: { ...e.protokoll, vorgaenger_id: data?.id ?? null } }));
    })();
    return () => { abgebrochen = true; };
  }, [protokoll.art, protokoll.einheit_id, setEntwurf]);

  // Der Wechsel der Art verschiebt die Vermieterseite (einzug = Übergeber, auszug =
  // Übernehmer, kauf = keine). Ein bereits gesetztes `nur_unterzeichner` hinge danach an
  // der falschen Rolle und würde eine echte Partei ohne Mailadresse stillstellen — deshalb
  // wird es hier von jeder Rolle entfernt, die unter der neuen Art keine Vermieterseite
  // mehr ist. Für die neue Vermieterseite wird es NICHT nachgesetzt; es entsteht
  // ausschließlich beim Rückschreiben einer Unterschrift (s. lib/uebergabe/unterschriften.ts).
  const aendereArt = (art: Art) => setEntwurf(e => ({
    ...e,
    protokoll: { ...e.protokoll, art, parteien: entferneUnpassendeMerkmale(e.protokoll.parteien, art) },
  }));
  const aendereObjekt = (objektId: string) => setEntwurf(e => ({
    ...e, protokoll: { ...e.protokoll, objekt_id: objektId || null, einheit_id: null },
  }));
  const aendereEinheit = (einheitId: string) => setEntwurf(e => ({
    ...e, protokoll: { ...e.protokoll, einheit_id: einheitId || null },
  }));
  const aendereDatum = (wert: string) => setEntwurf(e => ({
    ...e, protokoll: { ...e.protokoll, durchgefuehrt_am: datetimeLocalZuIso(wert) },
  }));
  // Rollout-Fund 4: Das Ändern läuft über setzeParteiFeld statt über ein map() hier, damit
  // das Merkmal `nur_unterzeichner` verschwindet, sobald jemand Anschrift, Mail oder Telefon
  // nachträgt — wer erreichbar ist, ist kein reiner Unterzeichner mehr, und seine fehlende
  // Mailadresse muss wieder gemeldet werden (s. lib/uebergabe/unterschriften.ts).
  //
  // CRITICAL 1 (Abschluss-Review): Übergibt die ganze PARTEI (nicht mehr nur ihre Rolle) —
  // setzeParteiFeld gleicht seither über die Kennung ab. Mit der Rolle allein trafen zwei
  // Zeugenkarten dieselbe Zeile: Tippte man in Karte 2, landete der Wert auch in Karte 1
  // (setzeParteiFeld mappte über `p.rolle !== rolle`, das galt für BEIDE Zeugen).
  const aenderePartei = (partei: Partei, feld: ParteiFeld, wert: string) => setEntwurf(e => ({
    ...e,
    protokoll: { ...e.protokoll, parteien: setzeParteiFeld(e.protokoll.parteien, partei, feld, wert) },
  }));
  // Spec: Übernahme ersetzt die Karte komplett — alle vier Felder, auch mit ''. Ebenfalls
  // über die Kennung (s. aenderePartei) statt über die Rolle.
  const uebernehmePerson = (partei: Partei, t: PersonTreffer) => setEntwurf(e => {
    let parteien = e.protokoll.parteien;
    parteien = setzeParteiFeld(parteien, partei, 'name', t.name);
    parteien = setzeParteiFeld(parteien, partei, 'anschrift', t.anschrift);
    parteien = setzeParteiFeld(parteien, partei, 'email', t.email);
    parteien = setzeParteiFeld(parteien, partei, 'telefon', t.telefon);
    return { ...e, protokoll: { ...e.protokoll, parteien } };
  });

  const objektAusgewaehlt = objekte.find(o => o.id === protokoll.objekt_id) ?? null;

  return (
    <>
      <section aria-label="Rahmen" className="immo-glas" style={KARTE}>
        <span style={LABEL}>Art der Übergabe</span>
        <ArtKacheln wert={protokoll.art} onWahl={(a) => { if (!bezugGesperrt) aendereArt(a); }} gesperrt={bezugGesperrt} />

        <div className="grid" style={{ gap: 10, gridTemplateColumns: 'repeat(auto-fit, minmax(min(240px, 100%), 1fr))' }}>
          <div className="flex min-w-0 flex-col" style={{ gap: 6 }}>
            {objekteFehler ? (
              <LadeHinweis text="Objekte konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeObjekte} />
            ) : (
              <GlasAuswahl label="Objekt" ariaLabel="Objekt" icon={Buildings} wert={protokoll.objekt_id ?? ''} onChange={aendereObjekt} gesperrt={bezugGesperrt}
                optionen={[{ wert: '', label: 'Objekt wählen…' }, ...objekte.map(o => ({ wert: o.id, label: o.bezeichnung }))]} />
            )}
            {objektAusgewaehlt && (
              <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink2)', paddingLeft: 4 }}>
                {objektAusgewaehlt.strasse} {objektAusgewaehlt.hausnummer}, {objektAusgewaehlt.plz} {objektAusgewaehlt.stadt}
              </span>
            )}
          </div>
          <div className="flex min-w-0 flex-col">
            {einheitenFehler ? (
              <LadeHinweis text="Einheiten konnten nicht geladen werden — Verbindung prüfen." aufErneutVersuchen={ladeEinheiten} />
            ) : (
              <GlasAuswahl label="Einheit" ariaLabel="Einheit" icon={Door} wert={protokoll.einheit_id ?? ''} onChange={aendereEinheit} gesperrt={bezugGesperrt || !protokoll.objekt_id}
                optionen={[{ wert: '', label: einheitenLaden ? 'Lädt…' : 'Einheit wählen…' }, ...einheiten.map(e => ({ wert: e.id, label: e.bezeichnung }))]} />
            )}
          </div>
          <label className="immo-glas flex min-w-0 flex-col justify-center" style={{ height: 58, borderRadius: 16, padding: '0 14px', gap: 2 }}>
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)' }}>Datum / Uhrzeit</span>
            <input type="datetime-local" value={isoZuDatetimeLocal(protokoll.durchgefuehrt_am)} onChange={e => aendereDatum(e.target.value)} aria-label="Datum / Uhrzeit"
              style={{ border: 'none', outline: 'none', background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 15, fontWeight: 800, color: 'var(--ink)', minWidth: 0 }} />
          </label>
        </div>
        {bezugGesperrt && (
          <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink3)' }}>Art, Objekt und Einheit legt die Verwaltung fest.</span>
        )}
      </section>

      <span style={{ ...LABEL, fontSize: 13, paddingLeft: 4 }}>Parteien</span>
      <div className="grid items-start" style={{ gap: 12, gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))' }}>
        {protokoll.parteien.map(p => {
          // Neue Anschrift nach Auszug: nur beim Übergeber und nur, wenn fachlich jemand auszieht —
          // dieselbe Bedingung steht zentral in zeigtNeueAnschrift() (lib/uebergabe/unterschriften.ts).
          const zeigeNeueAnschrift = zeigtNeueAnschrift(protokoll.art, p.rolle);
          const r = ROLLE_ICON[p.rolle];
          const RI = r.icon;
          // Unterzeile nur, wo der Code die Seite kennt (Freigabe 17.10.2026): Vermieterseite je Art.
          const unterzeile = istVermieterseite(protokoll.art, p.rolle) ? 'Vermieter-/Verwalterseite' : null;
          // CRITICAL 1 (Abschluss-Review): Schlüssel und Beschriftung über die Kennung statt über die Rolle.
          return (
            <section key={p.pid ?? p.rolle} aria-label={parteiLabel(p, protokoll.parteien)} className="immo-glas" style={{ ...KARTE, gap: 12 }}>
              <div className="flex items-center" style={{ gap: 10 }}>
                <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 32, height: 32, borderRadius: 10, background: r.farbe, color: '#fff' }}>
                  <RI size={18} weight="fill" />
                </span>
                <span className="flex min-w-0 flex-col">
                  <span style={{ fontSize: 15, fontWeight: 800 }}>{parteiLabel(p, protokoll.parteien)}</span>
                  {unterzeile && <span style={{ fontSize: 12, color: 'var(--ink3)' }}>{unterzeile}</span>}
                </span>
              </div>
              <ParteiSuche onWahl={(t) => uebernehmePerson(p, t)} />
              <IosGruppe ariaLabel={`Angaben ${parteiLabel(p, protokoll.parteien)}`}>
                <IosFeldZeile icon={User} placeholder="Name" value={p.name} onChange={e => aenderePartei(p, 'name', e.target.value)} />
                <IosFeldZeile icon={MapPin} placeholder="Anschrift" value={p.anschrift} onChange={e => aenderePartei(p, 'anschrift', e.target.value)} />
                <IosFeldZeile icon={EnvelopeSimple} placeholder="E-Mail" type="email" value={p.email} onChange={e => aenderePartei(p, 'email', e.target.value)} />
                <IosFeldZeile icon={Phone} placeholder="Telefon" type="tel" value={p.telefon} onChange={e => aenderePartei(p, 'telefon', e.target.value)} />
              </IosGruppe>
              {zeigeNeueAnschrift && (
                <div className="flex flex-col" style={{ gap: 6 }}>
                  <span style={LABEL}>Neue Anschrift (nach Auszug)</span>
                  <IosGruppe>
                    <IosFeldZeile icon={HouseLine} placeholder="Neue Anschrift (optional)" value={p.neue_anschrift ?? ''}
                      onChange={e => aenderePartei(p, 'neue_anschrift', e.target.value)} />
                  </IosGruppe>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </>
  );
};

export default SchrittRahmen;
